import cx from 'classnames'
import { useRouter } from 'next/router'
import * as React from 'react'

import { buildActionAnalyticsAttributes } from '@mondough/analytics-v2'
import { Copy, useCopy } from '@mondough/copy'
import { Text, Title } from '@mondough/monzo-ui'

import { ReferralContext } from '../../../providers'
import questionmarkIcon from '../../../public/images/icons/questionmark.circle.svg'
import toyIcon from '../../../public/images/icons/toy.svg'
import bba from '../../../public/images/trust/bba.png'
import cass from '../../../public/images/trust/cass.svg'
import fscs from '../../../public/images/trust/fscs-pos.svg'
import { NUMBER_OF_CUSTOMERS_MILLIONS, getCDNSrc } from '../../../utils'
import { AppStoreButton, Button } from '../../'
import styles from './MultipleBlockBanner.module.scss'

const Block = ({
  iconSrc,
  title,
  text,
  buttonText,
  buttonHref,
  openInNewTab,
  analyticsName,
  shouldDisplay = true,
  className,
}: {
  iconSrc: string
  title: string
  text: string
  buttonText: string
  buttonHref: string
  openInNewTab?: boolean
  analyticsName: string
  shouldDisplay?: boolean
  className?: string
}) => {
  const router = useRouter()

  return shouldDisplay ? (
    <article className={cx([styles.block, className])}>
      <Title is="h2" className={styles.title}>
        <img className={styles.icon} src={iconSrc} alt="" />
        {title}
      </Title>
      {text.split('\n').map((line) => (
        <Text key={line} className={styles.text}>
          {line}
        </Text>
      ))}
      <Button
        theme="primary"
        className={styles.button}
        onClick={async () => await router.push(buttonHref)}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        {...buildActionAnalyticsAttributes(analyticsName)}
      >
        {buttonText}
      </Button>
    </article>
  ) : null
}

const MultipleBlockBanner = () => {
  const { referralData } = React.useContext(ReferralContext)
  const router = useRouter()
  const c = useCopy()

  return (
    <div className={styles.inner}>
      <Block
        iconSrc={getCDNSrc(toyIcon)}
        title={c.string({
          key: 'app_tabs_monzo_me.marketing.banner_discover.title',
          message: 'Discover Monzo',
        })}
        text={c.string({
          key: 'app_tabs_monzo_me.marketing.banner_discover.description',
          message:
            'Join {{ numberOfMillionCustomers }} million people banking with Monzo',
          variables: {
            numberOfMillionCustomers: NUMBER_OF_CUSTOMERS_MILLIONS,
          },
        })}
        buttonText={c.string({
          key: 'app_tabs_monzo_me.marketing.banner_discover.button',
          message: 'See what you can do',
        })}
        buttonHref="https://monzo.com/"
        openInNewTab={true}
        analyticsName="banner-discover"
        className={referralData ? 'desktop-only' : 'mobile-only'}
      />
      <Block
        iconSrc={getCDNSrc(questionmarkIcon)}
        title={c.string({
          key: 'app_tabs_monzo_me.marketing.banner_faq.title',
          message: 'Got questions?',
        })}
        text={c.string({
          key: 'app_tabs_monzo_me.marketing.banner_faq.text',
          message: 'Check out our frequently asked questions',
        })}
        buttonText={c.string({
          key: 'app_tabs_monzo_me.marketing.banner_faq.button',
          message: 'See FAQs',
        })}
        buttonHref="/faq"
        analyticsName="banner-faq"
        shouldDisplay={!router.pathname.includes('faq')}
      />
      <article className={cx([styles['download-block'], 'mobile-only'])}>
        <Title
          is="h2"
          color="white"
          size="large"
          align="center"
          className={styles.title}
        >
          <Copy.Simple
            id="app_tabs_monzo_me.marketing.download_block.title"
            fallback="Open a free bank account"
          />
        </Title>
        <div className={styles['app-buttons']}>
          <AppStoreButton href="https://monzo.com/download" store="app-store" />
          <AppStoreButton
            href="https://monzo.com/download"
            store="google-play"
          />
        </div>
      </article>

      <ul className={cx([styles['trust-grid'], 'mobile-only'])}>
        <li>
          <img
            src={getCDNSrc(bba)}
            alt={c.string({
              key: 'app_tabs_monzo_me.marketing.trust_grid.bba.alt',
              message: 'British Bank Awards Best Banking App Winner 2023',
            })}
          />
        </li>
        <li>
          <img
            src={getCDNSrc(cass)}
            alt={c.string({
              key: 'app_tabs_monzo_me.marketing.trust_grid.cass.alt',
              message: 'Current Account Switch Guarantee',
            })}
          />
        </li>
        <li>
          <img
            src={getCDNSrc(fscs)}
            alt={c.string({
              key: 'app_tabs_monzo_me.marketing.trust_grid.fscs.alt',
              message: 'Financial Services Compensation Scheme Protected',
            })}
          />
        </li>
      </ul>
    </div>
  )
}

export default MultipleBlockBanner
